import { OutShipmentStatus, type PharmacyOrderSortBy } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

// Rows fetched per page. The endpoint is cursor-paginated; the table loads more
// on scroll via NextCursor.
export const ORDERS_PAGE_SIZE = 25

export type BadgeVariant = 'default' | 'error' | 'warning' | 'active' | 'success' | 'idle'

// Maps each backend shipment status to a display label and Badge styling. The API
// may also send a ShipmentStatusDescription which, when present, takes precedence
// over `label` (see StatusBadge).
export const SHIPMENT_STATUS_META: Record<OutShipmentStatus, { label: string; variant: BadgeVariant }> = {
  [OutShipmentStatus.preparing]: { label: 'Preparing', variant: 'idle' },
  [OutShipmentStatus.shipped]: { label: 'Shipped', variant: 'active' },
  [OutShipmentStatus.outfordelivery]: { label: 'Out for delivery', variant: 'active' },
  [OutShipmentStatus.delivered]: { label: 'Delivered', variant: 'success' },
  [OutShipmentStatus.exceptionreported]: { label: 'Exception', variant: 'error' },
  [OutShipmentStatus.voided]: { label: 'Voided', variant: 'default' },
}

// Order in which status filter options are presented.
export const SHIPMENT_STATUS_ORDER: OutShipmentStatus[] = [
  OutShipmentStatus.preparing,
  OutShipmentStatus.shipped,
  OutShipmentStatus.outfordelivery,
  OutShipmentStatus.delivered,
  OutShipmentStatus.exceptionreported,
  OutShipmentStatus.voided,
]

export type SortKey = 'mrn' | 'prescriber' | 'rxCreated' | 'orderCreated' | 'cost'
export type SortDirection = 'asc' | 'desc'

export type SortState = { key: SortKey; direction: SortDirection }

export const DEFAULT_SORT: SortState = { key: 'orderCreated', direction: 'desc' }

// Maps a (column, direction) pair to the backend PharmacyOrderSortBy enum value.
// Enum ordering is defined by the API contract:
// 0 OrderCreatedDesc, 1 OrderCreatedAsc, 2 RxCreatedDesc, 3 RxCreatedAsc,
// 4 PatientMrnAsc, 5 PatientMrnDesc, 6 PrescriberAsc, 7 PrescriberDesc,
// 8 CostAsc, 9 CostDesc.
export const SORT_BY_ENUM: Record<SortKey, Record<SortDirection, PharmacyOrderSortBy>> = {
  orderCreated: { desc: 0, asc: 1 },
  rxCreated: { desc: 2, asc: 3 },
  mrn: { asc: 4, desc: 5 },
  prescriber: { asc: 6, desc: 7 },
  cost: { asc: 8, desc: 9 },
}

// Only these columns are sortable — the backend supports sorting on exactly these.
// Shipment status and carrier are intentionally not sortable.
export const SORTABLE_COLUMNS: Record<SortKey, string> = {
  mrn: 'Patient MRN',
  prescriber: 'Prescriber',
  rxCreated: 'Rx created',
  orderCreated: 'Order created',
  cost: 'Cost',
}
